import { useGetBookDetailsQuery } from '../api/booksApi';
import { Spinner } from '../../../components/Spinner';
import { Button } from '../../../components/Button';
import { ArrowLeftIcon } from '@heroicons/react/16/solid';
import { sendToWs } from '../../chat/api/chatApi';
import { ChatMessageTypes } from '../../chat/api/types';
import { Helmet } from 'react-helmet';
import { useSelector } from 'react-redux';
import { authSelector } from '../../auth/state/authSlice';
import { Link, useParams } from 'react-router-dom';

export const BookDetails = () => {
  const { bookId = '' } = useParams();
  const { data: book, isLoading } = useGetBookDetailsQuery({ bookId });
  const { token } = useSelector(authSelector);
  const isAuthenticated = Boolean(token);

  if (isLoading) {
    return <Spinner />;
  }

  const {
    cover,
    back,
    title,
    author,
    price,
    owner,
    category,
    pages,
    language,
    synopsis,
    is_owner
  } = book || {};

  const startChat = () => {
    sendToWs({
      msg_type: ChatMessageTypes.StartChat,
      recipient: String(owner?.id)
    });
  };

  return (
    <>
      <Helmet>
        <title>{title}</title>
        <meta name="description" content={`${title} by ${author}`} />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={`${title} by ${author}`} />
        <meta property="og:image" content={cover} />
        <meta property="og:type" content="book" />
        <meta property="og:url" content={window.location.href} />
        <link rel="canonical" href={window.location.href} />
        <link rel="icon" href={cover} />
      </Helmet>
      <div>
        <div className="flex w-52 mb-3">
          <Link to={is_owner ? '/my-books' : '/books'}>
            <Button>
              <ArrowLeftIcon className="size-5 fill-white" />
              Back to {is_owner ? 'My Books' : 'Books'}
            </Button>
          </Link>
        </div>
        <div className="flex flex-col md:flex-row gap-6">
          <div className="flex flex-col gap-3 w-full md:w-1/2">
            <img src={cover} alt={title} className="w-full h-full object-cover rounded-xl" />
            {back && (
              <img src={back} alt={title} className="w-full h-full object-cover rounded-xl" />
            )}
          </div>
          <div className="flex flex-col gap-3 w-full md:w-1/2">
            <h1 className="text-3xl font-bold">{title}</h1>
            <h3 className="text-xl font-semibold text-stone-500">{author}</h3>
            {price && <h3 className="text-xl font-semibold text-green-600">€{price}</h3>}
            <CardSection title="About this book">
              <div className="flex flex-col gap-3">
                <p>{synopsis}</p>
                {pages && (
                  <p>
                    Pages: <span className="font-bold">{pages}</span>
                  </p>
                )}
                {language && (
                  <p>
                    Language: <span className="font-bold">{language?.toLocaleUpperCase()}</span>
                  </p>
                )}
                <p>
                  Category: <span className="font-bold">{category}</span>
                </p>
              </div>
            </CardSection>
            <CardSection title="Owner">
              <p>
                {owner?.first_name} {owner?.last_name}
              </p>
            </CardSection>
            {!is_owner && (
              <div className="flex flex-col gap-3">
                <div className="flex items-center gap-3">
                  <Button disabled={!isAuthenticated} onClick={startChat}>
                    Chat with {owner?.first_name} {owner?.last_name}
                  </Button>
                  {/* <Button disabled={!isAuthenticated}>Send reservation request</Button> */}
                </div>
                {!isAuthenticated && (
                  <div>
                    <p className="text-stone-500">
                      Please{' '}
                      <Link to="/login" className="underline font-bold text-cyan-800">
                        sign in
                      </Link>{' '}
                      to chat with the owner
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

type CardSectionProps = {
  title: string;
  children: React.ReactNode;
};

const CardSection = ({ title, children }: CardSectionProps) => {
  return (
    <div className="p-4 rounded-xl border border-stone-300">
      <h2 className="mb-2 text-xl font-semibold">{title}</h2>
      {children}
    </div>
  );
};
